import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BizDataGridDirective } from 'src/app/shared/grid/biz-grid.directive';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DataField } from '../models/data-field';
import { TrackingField } from '../models/tracking-field';
import { DeveloperService } from '../services/developer.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeveloperTrackingFieldComponent } from '../developer-tracking-field/developer-tracking-field.component';
import { DataFieldGroup } from '../models/data-field-group';
import { DataFieldType } from '../models/data-field-type';
import { CurrentUserService } from 'src/app/user/services';
import { GenericModalService, ModalMultiButton, ModalSettingsConfirmMultiButton } from 'src/app/shared/generic-modal/generic-modal.service';
import { DocumentTypeDeveloper, LightTrackingOptions } from '../models/document-type';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective as BizDataGridDirective_1 } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-tracking-fields',
    templateUrl: './developer-tracking-fields.component.html',
    styleUrls: ['./developer-tracking-fields.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective_1, DxoEditingModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class DeveloperTrackingFieldsComponent implements OnInit, AfterViewInit {
    @ViewChild(BizDataGridDirective, { static: true }) dataGrid: BizDataGridDirective;

    fields: TrackingField[] = [];
    distros: DocumentTypeDeveloper[] = [];
    dataFieldGroups: DataFieldGroup[] = [];
    dataFieldTypes: DataFieldType[] = [];
    allowAdding = false;
    allowDeleting = false;
    allowEditing = false;
    allowShowLog = false;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private genericModalService: GenericModalService,
        private modalService: NgbModal,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        public bizGridFiltervalues: BizGridFiltervalues) {
        this.onAddTrackingField = this.onAddTrackingField.bind(this);
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_TrackingField_Create');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_TrackingField_Delete');
        this.allowEditing = this.currentUserService.hasPermission('Developer_Action_TrackingField_Edit');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    ngAfterViewInit(): void {
        void this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.fields = [];

            let fields: TrackingField[] = [];
            await Promise.all([
                this.developerService.getDocumentTypes().then(result => { this.distros = result.sort((d1, d2) => d1.Name.localeCompare(d2.Name)); }),
                this.developerService.getDataFieldGroups().then(result => { this.dataFieldGroups = result.sort((g1, g2) => g1.DisplayName.localeCompare(g2.DisplayName)); }),
                this.developerService.getAvailableTrackingFieldDataTypes().then(result => { this.dataFieldTypes = result; }),
                this.developerService.getTrackingFields().then(result => { fields = result; })
            ]);

            this.distros.splice(0, 0, {
                Id: null,
                Name: '(None)',
                UsableForDistributions: false,
                Description: '-',
                DocumentTypeGroupId: 0,
                CdmEmbeddedFileDataXPath: '',
                CdmEmbeddedFileNameXPath: '',
                CdmStylesheetArtifactId: null,
                FilenamingTypes: [],
                LightTrackingOptions: new LightTrackingOptions(),
                CreatedBy: null,
                CreatedDate: null,
                UpdatedBy: null,
                UpdatedDate: null
            });

            this.dataFieldGroups.splice(0, 0, {
                DataFieldGroupId: -1,
                Description: '',
                DisplayName: '(None)',
                SortOrder: 0
            });

            this.dataFieldTypes.splice(0, 0, {
                DateTypeId: -1,
                DataTypeName: '(None)',
                DataTypeEnum: 'Unknown'
            });

            // Ensure that these are set last
            this.fields = fields;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onToolbarPreparing(e: any): void {
        e.toolbarOptions.items.forEach((item: any) => {
            if (item.name === 'addRowButton') {
                item.options.onClick = this.onAddTrackingField;
            }
        });
    }

    async onAddTrackingField(_: any): Promise<void> {
        try {
            const modalRef = this.modalService.open(DeveloperTrackingFieldComponent, { backdrop: 'static', size: 'lg', centered: true });
            modalRef.componentInstance.distros = this.distros;
            modalRef.componentInstance.dataFieldGroups = this.dataFieldGroups;
            modalRef.componentInstance.dataFieldTypes = this.dataFieldTypes;
            modalRef.componentInstance.trackingField = this.initNewTrackingField();

            const result = await modalRef.result;
            if (result) {
                await this.saveTrackingField(result);
                await this.refresh();
            }
        }
        catch (err) {
            // We get here if the dialog is dismissed (canceled)
            await this.refresh();
        }
    }

    async editTrackingField(cellInfo: any): Promise<void> {
        try {
            const modalRef = this.modalService.open(DeveloperTrackingFieldComponent, { backdrop: 'static', size: 'lg', centered: true });
            modalRef.componentInstance.distros = this.distros;
            modalRef.componentInstance.dataFieldGroups = this.dataFieldGroups;
            modalRef.componentInstance.dataFieldTypes = this.dataFieldTypes;
            modalRef.componentInstance.trackingField = cellInfo.data;

            const result = await modalRef.result;
            if (result) {
                await this.saveTrackingField(result);
                await this.refresh();
            }
        }
        catch (err) {
            if (err) console.error(err);
            // We get here if the dialog is dismissed (canceled)
            await this.refresh();
        }
    }

    async saveTrackingField(field: TrackingField): Promise<TrackingField> {
        try {
            const newField = await this.developerService.saveTrackingField(field);
            field.TrackingFieldTypesId = newField?.TrackingFieldTypesId ?? field.TrackingFieldTypesId;
            field.DataField.DataFieldGroupsId = newField?.DataField?.DataFieldGroupsId ?? field.DataField.DataFieldGroupsId;
            field.DataField.DataFieldsId = newField?.DataField?.DataFieldsId ?? field.DataField.DataFieldsId;
            this.toastService.showToast('Tracking field saved...', ToastType.success);
            return newField;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save tracking field...', ToastType.error);
            return Promise.reject();
        }
    }

    async onRowRemoving(e: any): Promise<void> {
        const item = e.data as TrackingField;

        const deferred = $.Deferred();
        e.cancel = deferred.promise();

        try {
            await this.developerService.deleteTrackingField(item.TrackingFieldTypesId, err => err?.status !== 409);
            this.toastService.showToast('Tracking field deleted...', ToastType.success);
            deferred.resolve(false);
        }
        catch (err) {
            // Process 409 error here; other errors are processed using generic error handling
            if (err?.status === 409) {
                const settings = new ModalSettingsConfirmMultiButton('Delete failed', `Error details: <pre style='white-space: pre-wrap;'>${err.error}</pre>`);
                settings.size = 'lg';
                settings.backdrop = 'static';
                settings.buttons = [new ModalMultiButton('Close', null, 'btn btn-primary')];
                await this.genericModalService.confirmMultiButton(settings);
            }
            deferred.resolve(true);
        }
    }

    initNewTrackingField(): TrackingField {
        return {
            TrackingFieldTypesId: 0,
            Name: '',
            XPath: '',
            ContextProperty: null,
            ContextPropertyNamespace: null,
            Macro: null,
            DistributionObjectsId: -1,
            ShowInSearchPreview: false,
            DataField: {
                DataFieldsId: 0,
                DataTypesId: -1,
                DisplayName: '',
                IsRequired: false,
                DataLength: 10,
                DefaultValue: '',
                Regex: '',
                RegexErrorMessage: '',
                DataFieldGroupsId: -1,
                SortOrder: 1,
                InfoText: '',
                Choices: []
            } as DataField
        } as TrackingField;
    }

    private inferTrackingFieldType(field: TrackingField): 'Xpath' | 'Context' | 'Macro' {
        const macro = (field?.Macro ?? '').trim();
        if (macro.length > 0) return 'Macro';

        const contextProp = (field?.ContextProperty ?? '').trim();
        const contextNs = (field?.ContextPropertyNamespace ?? '').trim();
        if (contextProp.length > 0 || contextNs.length > 0) return 'Context';

        const xpath = (field?.XPath ?? '').trim();
        if (xpath.length > 0) return 'Xpath';

        return 'Xpath';
    }

    calculateType = (rowData: TrackingField): string => {
        return this.inferTrackingFieldType(rowData);
    };

    calculateValue = (rowData: TrackingField): string => {
        const type = this.inferTrackingFieldType(rowData);

        if (type === 'Macro') return rowData?.Macro ?? '';

        if (type === 'Context') {
            const cp = (rowData?.ContextProperty ?? '').trim();
            const ns = (rowData?.ContextPropertyNamespace ?? '').trim();
            return ns.length > 0 ? `${ns}#${cp}` : cp;
        }

        return rowData?.XPath ?? '';
    };
}
